import React, { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  InputAdornment,
  TextField,
  IconButton,
  Popover,
  PopoverOrigin,
  Button
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  Event as CalendarToday,
  Clear as ClearIcon
} from '@mui/icons-material';
import { CalendarPicker } from '@mui/x-date-pickers/CalendarPicker';
import { FormattedMessage, useIntl } from 'react-intl';
import { ClassNameMap } from '@mui/styles';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import { useMeContext } from 'modules/me';
import { isWeekend } from 'utils/date-time';

type CalendarDateWithToday = {
  testId: string;
  classes?: ClassNameMap;
  value: DateTime | null;
  endIcon?: React.ReactNode;
  hasEndAdornment: boolean;
  showClearEndAdornment?: boolean;
  onClearDate?: (event: React.MouseEvent<HTMLElement>) => void;
  inputVariant?: 'filled' | 'standard' | 'outlined';
  textFieldStyle?: React.CSSProperties;
  handleDateChange?(date: DateTime | null): void;
  minDate?: DateTime;
  maxDate?: DateTime;
  disableUnderline?: boolean;
  fullWidth?: boolean;
  fitContent?: boolean;
  className?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: React.ReactNode;
};

const useStyles = makeStyles((theme) => ({
  dateSelector: {
    margin: theme.spacing(0),
    color: theme.palette.grey[900],
    opacity: 0.5
  },
  inputField: {
    cursor: 'pointer',
    caretColor: 'transparent',
    ...theme.typography.body2
  },
  weekend: {
    backgroundColor: theme.palette.grey[100]
  },
  goToTodayBtn: {
    display: 'flex',
    marginTop: theme.spacing(1),
    marginLeft: 'auto',
    marginRight: 'auto',
    textTransform: 'none'
  },
  input: { cursor: 'pointer' },
  clear: {
    padding: theme.spacing(0)
  }
}));

const usePopoverStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(0, 0, 1.5, 0)
  }
}));

const useCalenderStyles = makeStyles((theme) => ({
  viewTransitionContainer: {
    '& .PrivatePickersSlideTransition-root': {
      minHeight: theme.spacing(29.75)
    }
  }
}));

const usePickerDayStyles = makeStyles((theme) => ({
  root: {
    ...theme.typography.body2,
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  }
}));

const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center'
};
const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center'
};

export const CalendarDateWithToday: React.FC<CalendarDateWithToday> = ({
  testId,
  value,
  classes: overrideCls,
  endIcon,
  hasEndAdornment = false,
  showClearEndAdornment = false,
  onClearDate,
  inputVariant,
  handleDateChange,
  minDate,
  maxDate,
  disableUnderline,
  fullWidth,
  fitContent,
  className,
  disabled,
  size = 'small',
  label
}: CalendarDateWithToday) => {
  const classes = useStyles({ classes: overrideCls });
  const popoverClasses = usePopoverStyles();
  const calendarClasses = useCalenderStyles();
  const pickerDayClasses = usePickerDayStyles();

  const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      if (disabled) return;
      setAnchorEl(event.currentTarget);
    },
    [disabled]
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const me = useMeContext();
  const { formatMessage } = useIntl();

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const luxonFormat =
    me?.locale?.dateTimeSettings?.shortDateFormat?.luxonPattern ||
    'MMM d, yyyy';
  const endAdornmentProp = useMemo(
    () =>
      hasEndAdornment &&
      (value && showClearEndAdornment ? (
        <InputAdornment position="end">
          <IconButton
            onClick={onClearDate}
            className={classes.clear}
            size="large"
          >
            <ClearIcon />
          </IconButton>
        </InputAdornment>
      ) : (
        <InputAdornment position={'end'} className={classes.dateSelector}>
          {endIcon ? endIcon : <CalendarToday />}
        </InputAdornment>
      )),
    [
      hasEndAdornment,
      value,
      showClearEndAdornment,
      classes.clear,
      classes.dateSelector,
      onClearDate,
      endIcon
    ]
  );

  const formattedValue = useMemo(
    () => (value ? value.toFormat(luxonFormat) : ''),
    [luxonFormat, value]
  );
  const inputProps = useMemo(
    () => ({
      style: {
        width: fitContent ? `${formattedValue.length + 7}ch` : undefined
      },
      className: classes.inputField,
      endAdornment: endAdornmentProp,
      'aria-label': formatMessage({ id: 'note.selectDate' }),
      disableUnderline
    }),
    [
      classes.inputField,
      disableUnderline,
      endAdornmentProp,
      formatMessage,
      formattedValue.length,
      fitContent
    ]
  );

  const customRenderDay = useCallback(
    (
      day: DateTime,
      _: (DateTime | null)[],
      props: PickersDayProps<DateTime>
    ): JSX.Element => {
      const weekend = isWeekend((day as DateTime)?.toJSDate());
      return (
        <div className={weekend ? classes.weekend : undefined}>
          <PickersDay
            {...props}
            classes={pickerDayClasses}
            className={weekend ? classes.weekend : undefined}
          />
        </div>
      );
    },
    [classes.weekend, pickerDayClasses]
  );

  const onDateChange = useCallback(
    (date: DateTime | null) => {
      handleClose();
      handleDateChange?.(date);
    },
    [handleDateChange]
  );
  const goToToday = useCallback(() => {
    onDateChange(DateTime.local());
  }, [onDateChange]);

  const inputFieldProps = useMemo(() => ({ className: classes.input }), [
    classes.input
  ]);

  const defaultDate = useMemo(() => {
    if (value) {
      return value;
    }
    const today = DateTime.local();

    if (minDate && minDate.toISODate() >= today.toISODate()) {
      return minDate;
    }

    if (maxDate && maxDate.toISODate() <= today.toISODate()) {
      return maxDate;
    }
    return today;
  }, [maxDate, minDate, value]);
  const disableToday = useMemo(() => {
    const today = DateTime.local().toISODate();
    return (
      value?.toISODate() === today ||
      (minDate && minDate.toISODate() > today) ||
      (maxDate && maxDate.toISODate() < today)
    );
  }, [maxDate, minDate, value]);
  return (
    <>
      <TextField
        data-testid={`date-picker-${testId}`}
        InputProps={inputProps}
        inputProps={inputFieldProps}
        variant={inputVariant}
        value={formattedValue}
        onClick={handleClick}
        fullWidth={fullWidth}
        className={className}
        disabled={disabled}
        size={size}
        label={label}
      />
      <Popover
        data-testid="date-picker"
        classes={popoverClasses}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <CalendarPicker<DateTime>
          classes={calendarClasses}
          date={defaultDate}
          renderDay={customRenderDay}
          minDate={minDate}
          maxDate={maxDate}
          onChange={onDateChange}
        />
        <Button
          data-testid="day-picker-today-btn"
          variant="contained"
          onClick={goToToday}
          color="primary"
          className={classes.goToTodayBtn}
          disabled={disableToday}
        >
          <FormattedMessage id="calendar.goToToday" />
        </Button>
      </Popover>
    </>
  );
};
